import React, { Suspense, lazy, useEffect, useState } from 'react';
import PreLaunchHome from './pages/PreLaunchHome';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { WishlistProvider } from './context/WishlistContext';
import { OrderAvailabilityProvider } from './context/OrderAvailabilityContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import LoadingSpinner from './components/common/LoadingSpinner';
import LocationPermissionModal from './components/common/LocationPermissionModal';
import CountrySelector from './components/common/CountrySelector';
import ScrollToTop from './components/common/ScrollToTop';
import Canonical from './components/Canonical';
import './index.css';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'));
// PreLaunchHome is imported eagerly (top of file) rather than lazy-loaded
// so first-time visitors see no Suspense spinner flash before the hero
// paints — critical while the site is in pre-launch marketing mode.
// Trade-off: the pre-launch bundle is small, so pulling it into the
// main chunk costs ~a handful of KB but eliminates a visible loader
// frame on cold loads.

// HomepageRouter — picks between the temporary pre-launch page and the
// full marketplace homepage based on `site_settings.homepage_mode`.
//
// First-paint priority:
//   1. localStorage cache from a previously-completed fetch. Returning
//      visitors get instant, correct render (99% of the time).
//   2. If no cache is available, render `null` (blank hero area under
//      the persistent Navbar + Footer) until the network fetch resolves.
//      We deliberately DO NOT fall back to an env var or a hard-coded
//      default — a wrong-mode default would flash the pre-launch page
//      on visitors whose admin is in marketplace mode (or vice versa),
//      which is the exact bug being fixed here.
//
// The fetch always runs so a mode flip by admin is picked up on the
// visitor's next request cycle; localStorage is silently kept in sync
// so subsequent reloads paint the right hero on frame 1.
const HomepageRouter = () => {
    const cached = (() => {
        try {
            const v = localStorage.getItem('bb_homepage_mode');
            return v === 'prelaunch' || v === 'marketplace' ? v : null;
        } catch (_) { return null; }
    })();
    const [mode, setMode] = useState(cached);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/homepage-mode`);
                if (!res.ok || cancelled) return;
                const d = await res.json();
                const next = d?.mode === 'prelaunch' ? 'prelaunch' : 'marketplace';
                if (!cancelled && next !== mode) setMode(next);
                try { localStorage.setItem('bb_homepage_mode', next); } catch (_) { /* ignore */ }
                // Growth Mode flag is bundled in this same response so the
                // rest of the app can branch behaviour with zero extra HTTP
                // round trips. Stored in localStorage so first paint on
                // future page loads already reflects the right setting.
                try { localStorage.setItem('bb_growth_mode', d?.growth_mode ? '1' : '0'); } catch (_) { /* ignore */ }
            } catch (_) { /* network error — keep cached/fallback choice */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Render decision — three states:
    //   * `mode === 'prelaunch'`   → PreLaunchHome
    //   * `mode === 'marketplace'` → Home (full marketplace)
    //   * `mode === null`          → nothing yet (waiting for the fetch
    //     that started in the effect above). Empty <div> keeps the
    //     Navbar + Footer visible around a blank hero area for the
    //     ~200ms fetch, which is preferable to flashing the wrong
    //     mode. Guaranteed short: the fetch is a single tiny JSON.
    if (mode === 'prelaunch') return <PreLaunchHome />;
    if (mode === 'marketplace') return <Home />;
    return <div aria-hidden="true" data-testid="homepage-mode-loading" />;
};
const Shop = lazy(() => import('./pages/Shop'));
const Origins = lazy(() => import('./pages/Origins'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Register = lazy(() => import('./pages/Register'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const BuyerDashboard = lazy(() => import('./pages/BuyerDashboard'));
const SellerDashboard = lazy(() => import('./pages/SellerDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminMissingFiles = lazy(() => import('./pages/AdminMissingFiles'));
const SellerStore = lazy(() => import('./pages/SellerStore'));
const ClaimShop = lazy(() => import('./pages/ClaimShop'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const StaticPage = lazy(() => import('./pages/StaticPage'));
const SellerRegister = lazy(() => import('./pages/SellerRegister'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ReviewOrder = lazy(() => import('./pages/ReviewOrder'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const FeaturedBiscuits = lazy(() => import('./pages/FeaturedBiscuits'));
const FeaturedShops = lazy(() => import('./pages/FeaturedShops'));
const Categories = lazy(() => import('./pages/Categories'));
const NewArrivals = lazy(() => import('./pages/NewArrivals'));
const SellersByLocation = lazy(() => import('./pages/SellersByLocation'));
const BecomeASeller = lazy(() => import('./pages/BecomeASeller'));
const Partners = lazy(() => import('./pages/Partners'));
const PartnersDashboard = lazy(() => import('./pages/PartnersDashboard'));
const ManufacturerSurvey = lazy(() => import('./pages/ManufacturerSurvey'));
const AwardsPage = lazy(() => import('./pages/AwardsPage'));
const Messages = lazy(() => import('./pages/Messages'));
const StakeholderPage = lazy(() => import('./pages/StakeholderPage'));
const BiscuitsByCountry = lazy(() => import('./pages/BiscuitsByCountry'));
const BiscuitPage = lazy(() => import('./pages/BiscuitPage'));
const BiscuitBrandsPage = lazy(() => import('./pages/BiscuitBrandsPage'));
const BiscuitsPage = lazy(() => import('./pages/BiscuitsPage'));
const SuppliersDirectory = lazy(() => import('./pages/SuppliersDirectory'));
const BlogList = lazy(() => import('./pages/BlogList'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
    const { user, loading, isAuthenticated } = useAuth();
    const location = useLocation();
    
    if (loading) {
        return <LoadingSpinner fullScreen />;
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    
    if (requiredRole) {
        if (requiredRole === 'seller' && user?.role !== 'seller' && user?.role !== 'admin') {
            return <Navigate to="/" replace />;
        }
        if (requiredRole === 'admin' && user?.role !== 'admin') {
            return <Navigate to="/" replace />;
        }
    }
    
    return children;
};

import ProductRequestFAB from './components/common/ProductRequestFAB';

// Layout wrapper
const Layout = ({ children, hideFooter = false }) => {
    const location = useLocation();

    // Legacy fire-once heartbeat kept for backward compat with the raw
    // request counter. The real analytics — first-party page-view
    // tracking — lives in the second effect below and fires ONCE per
    // SPA route change (matches real user behaviour, not API activity).
    useEffect(() => {
        try {
            if (typeof window === 'undefined') return;
            if (window.__bb_visitor_pinged) return;
            window.__bb_visitor_pinged = true;
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/homepage-mode`)
                .catch(() => { /* network noise — analytics is best-effort */ });
        } catch (_) { /* ignore */ }
    }, []);

    // First-party page-view analytics. One event per real navigation.
    // Session id lives in sessionStorage so a reload counts as the
    // same session; a new tab is a new session. 30-min inactivity
    // timeout matches the industry-standard bounce definition.
    useEffect(() => {
        try {
            if (typeof window === 'undefined') return;
            const KEY = 'bb_analytics_session';
            const TS_KEY = 'bb_analytics_session_ts';
            const now = Date.now();
            let sid = window.sessionStorage.getItem(KEY);
            const lastTs = parseInt(window.sessionStorage.getItem(TS_KEY) || '0', 10);
            if (!sid || (lastTs && now - lastTs > 30 * 60 * 1000)) {
                // eslint-disable-next-line no-undef
                sid = (crypto?.randomUUID?.() || (Math.random().toString(36).slice(2) + Date.now().toString(36)));
                window.sessionStorage.setItem(KEY, sid);
            }
            window.sessionStorage.setItem(TS_KEY, String(now));

            const payload = {
                event_type: 'pageview',
                path: location.pathname,
                referrer: document.referrer || null,
                session_id: sid,
            };
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/track`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                keepalive: true,
            }).catch(() => { /* best effort */ });
        } catch (_) { /* ignore */ }
    }, [location.pathname]);

    // Delegated click tracking. Only tracks clicks on elements that
    // opt in via `data-analytics-click="<label>"` — prevents runaway
    // event volume from every incidental click on the page while
    // still surfacing high-signal actions (Add to cart, Submit, etc.).
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handler = (ev) => {
            try {
                const el = ev.target.closest?.('[data-analytics-click]');
                if (!el) return;
                const label = el.getAttribute('data-analytics-click') || el.textContent?.trim().slice(0, 80) || '';
                const eid = el.getAttribute('data-testid') || el.id || '';
                const sid = window.sessionStorage?.getItem('bb_analytics_session') || null;
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/track`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        event_type: 'click',
                        path: window.location.pathname,
                        element_id: eid,
                        element_label: label,
                        session_id: sid,
                    }),
                    keepalive: true,
                }).catch(() => {});
            } catch (_) { /* ignore */ }
        };
        window.addEventListener('click', handler, { capture: true, passive: true });
        return () => window.removeEventListener('click', handler, { capture: true });
    }, []);

    return (
        <div className="min-h-screen flex flex-col bg-background w-full max-w-full overflow-x-clip">
            <Navbar />
            <main className="flex-1 w-full max-w-full">
                {children}
            </main>
            {!hideFooter && <Footer />}
            <ProductRequestFAB />
        </div>
    );
};

// App Router with auth callback detection
const AppRouter = () => {
    const location = useLocation();
    
    // Check URL fragment for session_id synchronously during render
    if (location.hash?.includes('session_id=')) {
        return (
            <Suspense fallback={<LoadingSpinner fullScreen />}>
                <AuthCallback />
            </Suspense>
        );
    }
    
    return (
        <Suspense fallback={<LoadingSpinner fullScreen />}>
            <Canonical />
            <Routes>
                {/* Public Routes */}
                {/* Homepage toggle: admin sets the mode in /admin/settings,
                    persisted in `site_settings.homepage_mode`. HomepageRouter
                    fetches it (with localStorage + env-var fallbacks) and
                    renders either the temporary pre-launch page or the full
                    marketplace homepage. Flipping to marketplace requires no
                    code change or redeploy. */}
                <Route
                    path="/"
                    element={
                        <Layout>
                            <HomepageRouter />
                        </Layout>
                    }
                />
                <Route path="/shop" element={<Layout><Shop /></Layout>} />
                <Route path="/shop/:category" element={<Layout><Shop /></Layout>} />
                <Route path="/origins" element={<Layout><Origins /></Layout>} />
                <Route path="/featured-biscuits" element={<Layout><FeaturedBiscuits /></Layout>} />
                <Route path="/featured-shops" element={<Layout><FeaturedShops /></Layout>} />
                <Route path="/categories" element={<Layout><Categories /></Layout>} />
                <Route path="/new-arrivals" element={<Layout><NewArrivals /></Layout>} />
                <Route path="/sellers" element={<Layout><SellersByLocation /></Layout>} />
                <Route path="/product/:shopSlug/:brandSlug/:productSlug" element={<Layout><ProductDetail /></Layout>} />
                <Route path="/product/:shopSlug/:productSlug" element={<Layout><ProductDetail /></Layout>} />
                <Route path="/product/:productId" element={<Layout><ProductDetail /></Layout>} />
                <Route path="/cart" element={<Layout><Cart /></Layout>} />
                <Route path="/login" element={<Layout><Login /></Layout>} />
                <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
                <Route path="/reset-password/:token" element={<Layout><ForgotPassword /></Layout>} />
                <Route path="/register" element={<Layout><Register /></Layout>} />
                <Route path="/verify-email" element={<Layout><VerifyEmail /></Layout>} />
                <Route path="/unsubscribe" element={<Layout><Unsubscribe /></Layout>} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                
                {/* Static Pages */}
                <Route path="/about" element={<Layout><StaticPage slug="about" /></Layout>} />
                <Route path="/contact" element={<Layout><StaticPage slug="contact" /></Layout>} />
                <Route path="/faq" element={<Layout><StaticPage slug="faq" /></Layout>} />
                <Route path="/terms" element={<Layout><StaticPage slug="terms" /></Layout>} />
                <Route path="/privacy" element={<Layout><StaticPage slug="privacy" /></Layout>} />
                <Route path="/returns" element={<Layout><StaticPage slug="returns" /></Layout>} />
                <Route path="/seller-policy" element={<Layout><StaticPage slug="seller-policy" /></Layout>} />
                <Route path="/become-a-seller" element={<Layout><BecomeASeller /></Layout>} />
                <Route path="/sell" element={<Layout><BecomeASeller /></Layout>} />
                <Route path="/partners" element={<Layout><Partners /></Layout>} />
                <Route path="/partners/dashboard" element={<Layout><PartnersDashboard /></Layout>} />
                <Route path="/manufacturer-survey" element={<Layout><ManufacturerSurvey /></Layout>} />
                <Route path="/awards" element={<Layout><AwardsPage /></Layout>} />
                
                {/* SEO Pillar Pages */}
                <Route path="/biscuit" element={<Layout><BiscuitPage /></Layout>} />
                <Route path="/biscuit-brands" element={<Layout><BiscuitBrandsPage /></Layout>} />
                <Route path="/biscuits" element={<Layout><BiscuitsPage /></Layout>} />
                
                {/* SEO Stakeholder Pages */}
                <Route path="/manufacturers" element={<Layout><StakeholderPage key="manufacturers" /></Layout>} />
                <Route path="/suppliers" element={<Layout><SuppliersDirectory /></Layout>} />
                <Route path="/bakers" element={<Layout><StakeholderPage key="bakers" /></Layout>} />
                <Route path="/buyers" element={<Layout><StakeholderPage key="buyers" /></Layout>} />
                <Route path="/lovers" element={<Layout><StakeholderPage key="lovers" /></Layout>} />
                <Route path="/wholesale-biscuits" element={<Layout><StakeholderPage key="wholesale-biscuits" /></Layout>} />
                <Route path="/artisan-biscuit-brands" element={<Layout><StakeholderPage key="artisan-biscuit-brands" /></Layout>} />
                <Route path="/gift-biscuit-brands" element={<Layout><StakeholderPage key="gift-biscuit-brands" /></Layout>} />
                
                {/* SEO Country Pages */}
                <Route path="/biscuits-by-country/:country" element={<Layout><BiscuitsByCountry /></Layout>} />
                {/* Country aliases — friendlier URLs surfaced from the homepage "View All" button. */}
                <Route path="/countries" element={<Layout><Origins /></Layout>} />
                <Route path="/country/:country" element={<Layout><BiscuitsByCountry /></Layout>} />
                
                {/* Blog */}
                <Route path="/blog" element={<Layout><BlogList /></Layout>} />
                <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
                
                {/* SEO Aliases */}
                <Route path="/biscuit-suppliers" element={<Layout><SuppliersDirectory /></Layout>} />
                
                {/* Protected Routes - Buyer */}
                <Route path="/checkout" element={
                    <ProtectedRoute>
                        <Layout hideFooter><Checkout /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/checkout/success" element={
                    <Layout><CheckoutSuccess /></Layout>
                } />
                <Route path="/account/*" element={
                    <ProtectedRoute>
                        <Layout><BuyerDashboard /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/dashboard" element={
                    <ProtectedRoute>
                        <Layout><BuyerDashboard /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/wishlist" element={
                    <ProtectedRoute>
                        <Layout><Wishlist /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/review/:orderId" element={
                    <ProtectedRoute>
                        <Layout><ReviewOrder /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/messages" element={
                    <ProtectedRoute>
                        <Layout><Messages /></Layout>
                    </ProtectedRoute>
                } />
                
                {/* Protected Routes - Seller */}
                <Route path="/become-seller" element={
                    <ProtectedRoute>
                        <Layout><SellerRegister /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/seller/*" element={
                    <ProtectedRoute requiredRole="seller">
                        <Layout><SellerDashboard /></Layout>
                    </ProtectedRoute>
                } />
                
                {/* Public Seller Store Page (must be after /seller/*) */}
                <Route path="/store/:sellerId" element={<Layout><SellerStore /></Layout>} />
                <Route path="/store/:sellerId/claim" element={<Layout><ClaimShop /></Layout>} />
                
                {/* Protected Routes - Admin */}
                {/* Specific admin sub-routes MUST be declared before the
                    /admin/* catch-all that mounts AdminDashboard, so
                    React Router matches the specific path first. */}
                <Route path="/admin/missing-files" element={
                    <ProtectedRoute requiredRole="admin">
                        <Layout><AdminMissingFiles /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/admin/*" element={
                    <ProtectedRoute requiredRole="admin">
                        <Layout><AdminDashboard /></Layout>
                    </ProtectedRoute>
                } />
                
                {/* 404 */}
                <Route path="*" element={
                    <Layout>
                        <div className="container-custom py-20 text-center">
                            <h1 className="text-4xl font-serif mb-4">Page Not Found</h1>
                            <p className="text-muted-foreground mb-8">The page you're looking for doesn't exist.</p>
                            <a href="/" className="text-primary hover:underline">Return Home</a>
                        </div>
                    </Layout>
                } />
            </Routes>
        </Suspense>
    );
};

function App() {
    // Global image error handler — catches ALL broken images via event delegation.
    // Replaces broken src with an inline SVG placeholder so no broken icons ever appear.
    useEffect(() => {
        const PRODUCT_FALLBACK = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"><rect fill="%23f5f0eb" width="400" height="400"/><circle cx="200" cy="170" r="60" fill="%23e0d5c8"/><circle cx="170" cy="160" r="6" fill="%23c4b5a4"/><circle cx="220" cy="155" r="5" fill="%23c4b5a4"/><circle cx="195" cy="185" r="7" fill="%23c4b5a4"/><circle cx="210" cy="140" r="4" fill="%23c4b5a4"/><text x="200" y="270" text-anchor="middle" fill="%23b8a99a" font-family="serif" font-size="18">Image unavailable</text></svg>');
        const BANNER_FALLBACK = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 400"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="%23fef3c7"/><stop offset="100%" stop-color="%23fed7aa"/></linearGradient></defs><rect fill="url(%23g)" width="800" height="400"/><text x="400" y="210" text-anchor="middle" fill="%23b8860b" font-family="serif" font-size="28" opacity="0.5">Biscuit Brand</text></svg>');

        // Build the proxy URL using the runtime backend env so dev/prod
        // both work without further config. The proxy server-side fetches
        // the image with realistic browser headers (origin-matching Referer
        // and a no-referrer fallback) plus a 24h disk cache.
        const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
        const toProxy = (origUrl) =>
            `${BACKEND}/api/img-proxy?url=${encodeURIComponent(origUrl)}`;

        const handler = (e) => {
            if (e.target.tagName !== 'IMG') return;
            const img = e.target;
            if (img.dataset.fallbackApplied) return;

            const currentSrc = img.src || '';

            // ──────────────────────────────────────────────────────────
            // Retry chain when an external image fails:
            //   stage 1  →  rewrite http:// to https:// (silently fixes
            //               mixed-content blocks) AND/OR retry through
            //               our backend image proxy. The proxy server-
            //               side spoofs origin-matching Referer + real
            //               browser headers, so wp-content hotlink rules
            //               and most bot-mitigation systems pass it.
            //   stage 2  →  give up and show the fallback (sibling div
            //               with initials, or inline SVG placeholder).
            // ──────────────────────────────────────────────────────────
            const isExternal =
                /^https?:\/\//i.test(currentSrc) &&
                !currentSrc.includes('/api/img-proxy') &&
                !currentSrc.startsWith('data:');

            if (!img.dataset.retriedRecovery && isExternal) {
                img.dataset.retriedRecovery = 'true';
                let nextSrc = currentSrc;
                if (nextSrc.startsWith('http://')) {
                    nextSrc = 'https://' + nextSrc.slice(7);
                }
                // Route through proxy so hotlink + bot protections are bypassed
                img.src = toProxy(nextSrc);
                return;
            }

            img.dataset.fallbackApplied = 'true';

            // Check for a sibling fallback element (used by seller cards with initials)
            const sibling = img.parentElement?.querySelector('[data-role="img-fallback"]');
            if (sibling) {
                img.style.display = 'none';
                sibling.style.display = 'flex';
                return;
            }

            // Determine fallback by aspect ratio / size context
            const parent = img.parentElement;
            const isSquare = parent?.classList.contains('aspect-square');
            const isBanner = parent?.classList.contains('aspect-[16/9]') ||
                parent?.classList.contains('h-64') || parent?.classList.contains('h-80');

            img.src = isBanner ? BANNER_FALLBACK : PRODUCT_FALLBACK;
            img.style.objectFit = isSquare ? 'cover' : 'contain';
        };

        document.addEventListener('error', handler, true);
        return () => document.removeEventListener('error', handler, true);
    }, []);

    // ─────────────────────────────────────────────────────────────────────
    // Image-load skeleton flag
    // ─────────────────────────────────────────────────────────────────────
    // Pairs with the `img:not([data-loaded])` CSS rule in App.css. The
    // global `load` event on <img> elements doesn't bubble, so we attach
    // a capture-phase listener at the document level (same pattern as
    // the error handler above) and stamp every img with `data-loaded="1"`
    // the instant the browser finishes fetching it. The CSS deselects
    // the loaded image and the shimmer disappears.
    //
    // We also sweep any images that are ALREADY decoded by the time this
    // effect runs (`img.complete && naturalWidth > 0`) — that covers
    // images served from the browser's HTTP cache where the load event
    // already fired before our listener was attached.
    useEffect(() => {
        const markLoaded = (img) => {
            if (img && img.tagName === 'IMG' && img.dataset.loaded !== '1') {
                img.dataset.loaded = '1';
            }
        };
        const onLoad = (e) => markLoaded(e.target);
        document.addEventListener('load', onLoad, true);
        // Sweep images that were already complete before this effect ran
        // (HTTP cache, SSR-prerendered, etc.) so we never leave a loaded
        // image shimmering.
        try {
            document.querySelectorAll('img').forEach((img) => {
                if (img.complete && img.naturalWidth > 0) markLoaded(img);
            });
        } catch (_) { /* noop */ }
        return () => document.removeEventListener('load', onLoad, true);
    }, []);

    // Referrer tracking — fires exactly once per browser session for the
    // very first navigation that has a `document.referrer` from outside
    // our own domain. Skips:
    //   * Empty referrer (direct hits, app-to-app, prerendered SSR)
    //   * Same-origin referrers (page-to-page nav, not a backlink)
    //   * Crawler-y UAs (basic guard so we don't pollute the table)
    // Stores in sessionStorage so SPA route changes don't re-fire. The
    // backend is the source of truth and also deduplicates by domain +
    // landing page, but this keeps network noise down.
    useEffect(() => {
        try {
            if (typeof window === 'undefined') return;
            if (sessionStorage.getItem('bb_ref_logged') === '1') return;
            const ref = document.referrer || '';
            if (!ref) return;
            let refHost = '';
            try { refHost = new URL(ref).hostname.toLowerCase().replace(/^www\./, ''); } catch (_) { return; }
            const ownHost = window.location.hostname.toLowerCase().replace(/^www\./, '');
            if (!refHost || refHost === ownHost) return;
            const ua = (navigator.userAgent || '').toLowerCase();
            if (/(bot|crawler|spider|preview|prerender|lighthouse|headless)/.test(ua)) return;
            const landing = window.location.pathname + (window.location.search || '');
            sessionStorage.setItem('bb_ref_logged', '1');
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/referrer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ referrer_url: ref, landing_page: landing }),
                keepalive: true,
            }).catch(() => { /* silent — never block the page */ });
        } catch (_) { /* never throw in tracker */ }
    }, []);

    return (
        <BrowserRouter>
            <ScrollToTop />
            <AuthProvider>
                <CurrencyProvider>
                    <OrderAvailabilityProvider>
                    <CartProvider>
                        <WishlistProvider>
                            <AppRouter />
                            <LocationPermissionModal />
                            <CountrySelector />
                            <Toaster position="top-right" richColors />
                        </WishlistProvider>
                    </CartProvider>
                    </OrderAvailabilityProvider>
                </CurrencyProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
